import type { FunctionComponent } from 'react';

import Icon from './Icon';
import type { IconProps } from './Icon';
import type { IconAlign } from './icon-constants';

const defaultMarginAmount = '0.35em'; // em is relative to the parent's font-size, so this should work for all sizes

const margin = {
  left: {
    marginLeft: defaultMarginAmount,
  },
  right: {
    marginRight: defaultMarginAmount,
  },
};

export type IconTextProps = {
  /**
   * Text to display next to icon
   */
  text: string;
  /**
   * Icon to render
   */
  icon: string;
  /**
   * Which side of the text the Icon should be rendered on
   */
  alignIcon?: IconAlign;
  /**
   * a CSS width to use for the margin between the icon and the text. If not set, a default margin size is used that's proportional to the font size.
   */
  marginAmount?: string;
} & IconProps;

/**
 * Renders an Icon + some text inline.
 * Supports icons on the right or the left of the text.
 *
 * @example
 * import { IconText, icons } from '../generic/Icon';
 *
 * const MyComponent = () => (
 *  <IconText
 *    icon={icons.infoCircle}
 *    text="This is interesting"
 *    alignIcon="left"
 *  />
 * );
 *
 * export default MyComponent;
 */
const IconText: FunctionComponent<IconTextProps> = ({
  text,
  icon,
  alignIcon = 'left',
  marginAmount,
  ...iconProps
}) => (
  <>
    {alignIcon === 'right' && (
      <span style={marginAmount ? { marginRight: marginAmount } : margin.right}>
        {text}
      </span>
    )}
    {/* eslint-disable-next-line react/jsx-props-no-spreading */}
    <Icon icon={icon} {...iconProps} />
    {alignIcon === 'left' && (
      <span style={marginAmount ? { marginLeft: marginAmount } : margin.left}>
        {text}
      </span>
    )}
  </>
);

export default IconText;
